import { useState, useEffect } from 'react'
import { useChat } from '../contexts/ChatContext'
import { searchUsers, listFriends, createPluginDM } from '../services/api'
import { formatTime } from '../utils/time'
import { avatarColorIndex, SearchBar, ListRow, Chip, CountBadge, UnreadDot } from '@ofself/shell'

const CHAT_API = import.meta.env.VITE_CHAT_API_URL || ''

function formatShortTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const diff = now - d
  if (diff < 86400000 && d.getDate() === now.getDate()) return formatTime(ts)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  if (diff < 7 * 86400000) return days[d.getDay()]
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function DMsPage({ onSelectDM }) {
  const { dmChannels, createDM, currentUser, unreadCounts, lastMessageMap, activeChannel } = useChat()
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [friends, setFriends] = useState([])
  const [loadingFriends, setLoadingFriends] = useState(true)
  const [personas, setPersonas] = useState([])
  const [openingPersona, setOpeningPersona] = useState(null)

  useEffect(() => {
    async function fetchFriends() {
      try {
        const res = await listFriends()
        setFriends(res.friends || [])
      } catch (err) {
        console.error('Failed to fetch friends:', err)
      } finally {
        setLoadingFriends(false)
      }
    }
    fetchFriends()
  }, [])

  useEffect(() => {
    async function fetchPlugins() {
      try {
        const token = localStorage.getItem('chat_session_token')
        const res = await fetch(`${CHAT_API}/api/chat/plugins/discover`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json()
          setPersonas(data.plugins || [])
        }
      } catch (err) {
        console.error('Failed to fetch plugins:', err)
      }
    }
    fetchPlugins()
  }, [])

  async function handleOpenPersonaDM(plugin) {
    setOpeningPersona(plugin.plugin_id)
    try {
      const res = await createPluginDM(plugin.plugin_id, plugin.name, plugin.sub_entity_key)
      if (res.channel) onSelectDM(res.channel)
    } catch (err) {
      console.error('Failed to open plugin DM:', err)
    } finally {
      setOpeningPersona(null)
    }
  }

  async function handleSearch(q) {
    setSearch(q)
    if (q.trim().length < 2) { setSearchResults([]); return }
    try {
      const res = await searchUsers(q.trim())
      const selectedIds = new Set(selectedUsers.map(u => u.user_id))
      setSearchResults((res.users || []).filter(u => u.user_id !== currentUser?.id && !selectedIds.has(u.user_id)))
    } catch (err) {
      console.error('Search failed:', err)
    }
  }

  function handleSelectUser(user) {
    setSelectedUsers(prev => [...prev, user])
    setSearch('')
    setSearchResults([])
  }

  function handleRemoveUser(userId) {
    setSelectedUsers(prev => prev.filter(u => u.user_id !== userId))
  }

  async function handleStartConversation() {
    if (selectedUsers.length === 0) return
    try {
      const userIds = selectedUsers.map(u => u.user_id)
      const usernames = {}
      selectedUsers.forEach(u => { if (u.username) usernames[u.user_id] = u.username })
      await createDM(userIds.length === 1 ? userIds[0] : userIds, usernames)
      setSelectedUsers([])
      setSearch('')
      setSearchResults([])
    } catch (err) {
      console.error('Failed to create DM:', err)
    }
  }

  async function handleCreateDM(userId, username) {
    try {
      const usernames = username ? { [userId]: username } : {}
      await createDM(userId, usernames)
      setSearch('')
      setSearchResults([])
    } catch (err) {
      console.error('Failed to create DM:', err)
    }
  }

  const getColor = (name) => `var(--avatar-${avatarColorIndex(name)})`

  const dmDisplayName = (dm) => {
    if (dm.is_group_dm && dm.member_usernames?.length > 0) {
      return dm.member_usernames.map(m => m.username).join(', ')
    }
    return dm.other_username || dm.name || 'Unknown'
  }

  const sortedDMs = [...dmChannels].sort((a, b) => {
    const tA = lastMessageMap[a.id]?.timestamp
    const tB = lastMessageMap[b.id]?.timestamp
    if (!tA && !tB) return 0
    if (!tA) return 1
    if (!tB) return -1
    return new Date(tB) - new Date(tA)
  })

  const filteredDMs = search.trim() && selectedUsers.length === 0
    ? sortedDMs.filter(dm => (dmDisplayName(dm)).toLowerCase().includes(search.toLowerCase()))
    : sortedDMs

  const existingDmUserIds = new Set(dmChannels.map(dm => dm.other_user_id).filter(Boolean))
  const filteredFriends = friends.filter(f => {
    if (f.user_id === currentUser?.id) return false
    if (existingDmUserIds.has(f.user_id)) return false
    if (search.trim().length >= 2) return (f.username || '').toLowerCase().includes(search.toLowerCase())
    return true
  })

  return (
    <div className="sidebar">
      <SearchBar
        value={search}
        onChange={e => handleSearch(e.target.value)}
        onClear={() => { setSearch(''); setSearchResults([]) }}
        placeholder={selectedUsers.length > 0 ? 'Add more people...' : 'Search conversations...'}
      >
        {selectedUsers.length > 0 && (
          <div className="chips-row">
            {selectedUsers.map(u => (
              <Chip key={u.user_id} onRemove={() => handleRemoveUser(u.user_id)}>
                {u.username}
              </Chip>
            ))}
          </div>
        )}
      </SearchBar>

      {selectedUsers.length > 0 && (
        <div className="dm-start-action">
          <button className="dm-start-btn" onClick={handleStartConversation}>
            Start Conversation{selectedUsers.length > 1 ? ` (${selectedUsers.length} people)` : ''}
          </button>
        </div>
      )}

      {personas.length > 0 && !search.trim() && selectedUsers.length === 0 && (
        <div className="page-panel-section">
          <div className="page-panel-section-title ofself-label">Agents</div>
          {personas.map(agent => (
            <button
              key={agent.id}
              className="dm-page-item"
              onClick={() => handleOpenPersonaDM(agent)}
              disabled={openingPersona === agent.id}
            >
              <div className="dm-page-avatar" style={{ position: 'relative' }}>
                {(agent.name || 'A')[0].toUpperCase()}
                <span style={{
                  position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderRadius: '50%',
                  background: '#566748', border: '2px solid var(--bg-primary, #fff)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="6" height="6" viewBox="0 0 24 24" fill="#fff" stroke="none">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.07A7.001 7.001 0 0 1 14 23h-4a7.001 7.001 0 0 1-6.93-4H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
                  </svg>
                </span>
              </div>
              <div className="dm-page-info">
                <span className="dm-page-name">{agent.name}</span>
                {agent.description && (
                  <span style={{ fontSize: 'var(--ofself-text-sm)', color: 'var(--text-muted)', marginTop: 1, display: 'block' }}>
                    {agent.description.slice(0, 50)}
                  </span>
                )}
              </div>
              <span style={{ fontSize: 'var(--ofself-text-sm)', color: 'var(--accent)', fontWeight: 'var(--ofself-weight-semibold)' }}>
                {openingPersona === agent.id ? '...' : 'Message'}
              </span>
            </button>
          ))}
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="page-panel-section">
          <div className="page-panel-section-title ofself-label">
            {selectedUsers.length > 0 ? 'Add to conversation' : 'Start a conversation'}
          </div>
          {searchResults.map(user => (
            <button
              key={user.user_id}
              className="dm-page-item"
              onClick={() => selectedUsers.length > 0 ? handleSelectUser(user) : handleCreateDM(user.user_id, user.username)}
            >
              <div className="dm-page-avatar" style={{ background: getColor(user.username || '?') }}>
                {(user.username || '?')[0].toUpperCase()}
              </div>
              <div className="dm-page-info">
                <span className="dm-page-name">{user.username}</span>
              </div>
              {selectedUsers.length > 0 && <span className="dm-add-icon">+</span>}
            </button>
          ))}
        </div>
      )}

      <ul className="block__body block-scroll list-rows" style={{ padding: '16px 0 0' }}>
        {filteredDMs.map(dm => {
          const name = dmDisplayName(dm)
          const isActive = activeChannel?.id === dm.id
          const hasUnread = !!unreadCounts[dm.id]
          const lastMsg = lastMessageMap[dm.id]
          const preview = lastMsg?.snippet || ''
          const timestamp = lastMsg?.timestamp
          const senderName = lastMsg?.senderName
          const unreadCount = typeof unreadCounts[dm.id] === 'number' && unreadCounts[dm.id] > 1
            ? unreadCounts[dm.id] : 0

          return (
            <ListRow
              key={dm.id}
              avatar={
                <div className="convo-avatar" style={{ background: getColor(name) }}>
                  {(name[0] || '?').toUpperCase()}
                </div>
              }
              name={name}
              preview={preview ? (senderName ? `${senderName}: ${preview}` : preview) : undefined}
              time={timestamp ? formatShortTime(timestamp) : undefined}
              trailing={hasUnread
                ? (unreadCount > 1 ? <CountBadge count={unreadCount} /> : <UnreadDot />)
                : undefined}
              active={isActive}
              unread={hasUnread}
              onClick={() => onSelectDM(dm)}
            />
          )
        })}

        {filteredFriends.length > 0 && (
          <>
            <div className="page-panel-section-title ofself-label" style={{ padding: '12px 16px 4px' }}>
              Friends
            </div>
            {filteredFriends.map(friend => (
              <ListRow
                key={friend.user_id}
                avatar={
                  <div className="convo-avatar" style={{ background: getColor(friend.username || '?') }}>
                    {(friend.username || '?')[0].toUpperCase()}
                  </div>
                }
                name={friend.username}
                preview={
                  friend.mutual ? 'Mutual friend'
                  : friend.direction === 'following' ? 'Following'
                  : friend.direction === 'follower' ? 'Follows you'
                  : undefined
                }
                onClick={() => handleCreateDM(friend.user_id, friend.username)}
              />
            ))}
          </>
        )}
      </ul>
    </div>
  )
}
